import { X, Car, Fuel, Users, Settings as SettingsIcon, ShieldCheck, MapPin, AlertTriangle, Tag, Hash, Cog, ListChecks, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { PublicVehicleGallery } from "../PublicFleetPhotos";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

const peso = (n) =>
  n == null || n === "" ? null : `₱${Number(n).toLocaleString()}`;

/** Public-safe detail modal for a vehicle.
 *
 *  `v` is the per-vehicle payload from `/api/public/rentals/:slug`: it
 *  already excludes internal_notes / maintenance_notes / staff_notes.
 *  Damage & incident history is rendered as a *gated* CTA card.
 */
export default function PublicVehicleDetailModal({ companyId, vehicle: v, onClose }) {
  const { panelRef } = useAccessibleOverlay(true, onClose);
  const specs = [
    v.make && { icon: <Tag className="w-3.5 h-3.5" />,        label: "Make",         value: v.make },
    v.model && { icon: <Hash className="w-3.5 h-3.5" />,      label: "Model",        value: [v.model, v.variant].filter(Boolean).join(" ") },
    v.year && { icon: <Tag className="w-3.5 h-3.5" />,        label: "Year",         value: v.year },
    v.color && { icon: <Tag className="w-3.5 h-3.5" />,       label: "Color",        value: v.color },
    v.transmission && { icon: <Cog className="w-3.5 h-3.5" />, label: "Transmission", value: v.transmission },
    v.fuel_type && { icon: <Fuel className="w-3.5 h-3.5" />,   label: "Fuel",         value: v.fuel_type },
    v.engine_cc && { icon: <Cog className="w-3.5 h-3.5" />,    label: "Engine",       value: `${v.engine_cc} cc` },
    v.seats && { icon: <Users className="w-3.5 h-3.5" />,      label: "Seats",        value: v.seats },
    v.doors && { icon: <SettingsIcon className="w-3.5 h-3.5" />, label: "Doors",      value: v.doors },
    v.has_aircon && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Air-conditioning", value: "Yes" },
    v.helmet_included && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Helmet", value: "Included" },
    v.luggage_capacity && { icon: <ShieldCheck className="w-3.5 h-3.5" />, label: "Luggage", value: v.luggage_capacity },
    v.plate_display && { icon: <Hash className="w-3.5 h-3.5" />, label: "Plate", value: v.plate_display },
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto"
         data-testid="public-vehicle-detail-modal">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="public-vehicle-detail-title"
             className="bg-white rounded-2xl border border-slate-200 max-w-3xl w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                {v.category || "Vehicle"}
              </div>
              <h2 id="public-vehicle-detail-title" className="font-display text-2xl font-semibold mt-0.5" data-testid="pvdm-title">{v.name}</h2>
              <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                {v.make && <span><Tag className="w-3 h-3 inline mr-0.5" />{v.make} {v.model} {v.variant}</span>}
                {v.year && <span>· {v.year}</span>}
                {v.color && <span>· {v.color}</span>}
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close vehicle details" className="p-1.5 rounded-md hover:bg-slate-100" data-testid="pvdm-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Gallery */}
            {(v.public_photos_count || 0) > 0 && (
              <PublicVehicleGallery companyId={companyId} vehicleId={v.id} />
            )}

            {/* Price + deposit */}
            <div className="flex flex-wrap gap-3" data-testid="pvdm-pricing">
              {peso(v.price_per_day) && (
                <Pill label="Per day" value={peso(v.price_per_day)} tone="emerald" testId="pvdm-price" />
              )}
              {peso(v.deposit_amount) && (
                <Pill label="Deposit" value={peso(v.deposit_amount)} tone="amber" testId="pvdm-deposit" />
              )}
              {v.status && (
                <Pill label="Availability" value={v.status} tone="slate" testId="pvdm-status" />
              )}
            </div>

            {/* Description */}
            {v.public_description && (
              <section data-testid="pvdm-description">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">About this vehicle</h3>
                <p className="mt-1.5 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{v.public_description}</p>
              </section>
            )}

            {/* Specs grid */}
            {specs.length > 0 && (
              <section data-testid="pvdm-specs">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Specifications</h3>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {specs.map((s, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 inline-flex items-center gap-1">
                        {s.icon} {s.label}
                      </div>
                      <div className="text-sm font-medium text-slate-900 mt-0.5 capitalize break-words">
                        {s.value}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Features */}
            {(v.public_features || []).length > 0 && (
              <Section title="Features" testId="pvdm-features" icon={<ListChecks className="w-3.5 h-3.5" />}>
                <ul className="mt-2 grid sm:grid-cols-2 gap-1 list-disc list-inside text-sm text-slate-700">
                  {v.public_features.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </Section>
            )}

            {(v.included_items || []).length > 0 && (
              <Section title="Included items" testId="pvdm-included" icon={<ShieldCheck className="w-3.5 h-3.5" />}>
                <ul className="mt-2 grid sm:grid-cols-2 gap-1 list-disc list-inside text-sm text-slate-700">
                  {v.included_items.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </Section>
            )}

            {v.recommended_use && (
              <Section title="Recommended use" testId="pvdm-recommended" icon={<Car className="w-3.5 h-3.5" />}>
                <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-line">{v.recommended_use}</p>
              </Section>
            )}

            {(v.allowed_travel_area || v.restricted_travel_area || v.pickup_delivery_notes || v.fuel_policy_note || v.mileage_limit) && (
              <Section title="Pickup, delivery & travel" testId="pvdm-travel" icon={<MapPin className="w-3.5 h-3.5" />}>
                <dl className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                  {v.pickup_delivery_notes && (<Definition label="Pickup / delivery" value={v.pickup_delivery_notes} />)}
                  {v.allowed_travel_area && (<Definition label="Allowed travel area" value={v.allowed_travel_area} />)}
                  {v.restricted_travel_area && (<Definition label="Restricted" value={v.restricted_travel_area} />)}
                  {v.fuel_policy_note && (<Definition label="Fuel policy" value={v.fuel_policy_note} />)}
                  {v.mileage_limit && (<Definition label="Mileage limit" value={v.mileage_limit} />)}
                </dl>
              </Section>
            )}

            {(v.requirements_to_rent || v.special_rules) && (
              <Section title="Rules & requirements" testId="pvdm-rules" icon={<AlertTriangle className="w-3.5 h-3.5" />}>
                <dl className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                  {v.requirements_to_rent && (<Definition label="Requirements" value={v.requirements_to_rent} />)}
                  {v.special_rules && (<Definition label="Special rules" value={v.special_rules} />)}
                </dl>
              </Section>
            )}

            {/* Damage / incident: gated section */}
            <DamageHistoryGate vehicleId={v.id} companyId={companyId} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, testId, icon, children }) {
  return (
    <section data-testid={testId}>
      <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
        {icon} {title}
      </h3>
      {children}
    </section>
  );
}

function Definition({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="text-slate-700 mt-0.5 whitespace-pre-line">{value}</dd>
    </div>
  );
}

function Pill({ label, value, tone = "slate", testId }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    slate:   "bg-slate-100 text-slate-800 border-slate-200",
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${map[tone]}`} data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">{label}</div>
      <div className="text-sm font-semibold capitalize">{value}</div>
    </div>
  );
}

function DamageHistoryGate({ vehicleId, companyId }) {
  // We render only the gated CTA. Actual access flow lives in
  // `/portal/passes` (Booking Confidence Pass): no records are leaked here.
  return (
    <section className="rounded-2xl border-2 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 to-amber-50/50 p-5"
             data-testid="pvdm-damage-gate">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 grid place-items-center flex-shrink-0">
          <Lock className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-slate-900">Damage & incident records</h3>
          <p className="text-sm text-slate-600 mt-1">
            See verified prior damage and incident history for this exact vehicle before you book.
            Unlock with a one-week pass or your active Booking Confidence Pass.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link
              to={`/portal/passes?vehicle=${encodeURIComponent(vehicleId)}&company=${encodeURIComponent(companyId)}`}
              data-testid="pvdm-damage-unlock"
              className="px-4 py-2 rounded-lg btn-brand text-sm font-medium inline-flex items-center gap-1.5"
            >
              <Lock className="w-3.5 h-3.5" /> Unlock vehicle history
            </Link>
            <Link
              to="/portal/passes"
              data-testid="pvdm-damage-learn"
              className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50"
            >
              Learn about Booking Confidence Pass
            </Link>
            <Link
              to="/login?next=/portal"
              data-testid="pvdm-damage-signin"
              className="px-4 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-900"
            >
              Sign in to check access
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
